import { useState, useCallback } from "react";
import type { PaginationMeta } from "@/types/common";

export interface UsePaginationReturn {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  resetPagination: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
}

export function usePagination(
  options: {
    initialPage?: number;
    initialLimit?: number;
  } = {},
  meta?: PaginationMeta
): UsePaginationReturn {
  const { initialPage = 1, initialLimit = 10 } = options;

  const [page, setPageState] = useState(initialPage);
  const [limit, setLimitState] = useState(initialLimit);

  const setPage = useCallback((newPage: number) => {
    setPageState(Math.max(1, newPage));
  }, []);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(Math.max(1, newLimit));
    setPageState(1);
  }, []);

  const nextPage = useCallback(() => {
    setPageState((prev) => prev + 1);
  }, []);

  const prevPage = useCallback(() => {
    setPageState((prev) => Math.max(1, prev - 1));
  }, []);

  const resetPagination = useCallback(() => {
    setPageState(initialPage);
    setLimitState(initialLimit);
  }, [initialPage, initialLimit]);

  const canGoNext = meta ? meta.hasNextPage : true;
  const canGoPrev = meta ? meta.hasPrevPage : page > 1;

  return {
    page,
    limit,
    setPage,
    setLimit,
    nextPage,
    prevPage,
    resetPagination,
    canGoNext,
    canGoPrev,
  };
}
